import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, ArrowLeft, CircleSlash, FileWarning, Info, OctagonX, RotateCw, ShieldCheck } from 'lucide-react';
import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ApplicantCard } from '@/components/applicant';
import { Celebrate } from '@/components/Celebrate';
import { CheckBadge, CheckStepper, DownloadReportButton, Elapsed, ResultCard, ScanningVisual } from '@/components/checks';
import { Page, Rise } from '@/components/motion';
import { ScreenshotPanel, ScreenshotReview } from '@/components/screenshot';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ErrorState, Skeleton } from '@/components/ui/misc';
import { checksApi, coinsApi } from '@/lib/api/endpoints';
import { isApiError, userMessage } from '@/lib/api/errors';
import type { Check } from '@/lib/api/types';
import { ME_KEY, useMe } from '@/lib/auth';
import { cn, formatDateTime } from '@/lib/utils';
import { canRetry, isInProgress, isVisaActive, OUTCOME_LABEL, summarizeCheck, type Tone } from '@/lib/visa';

const TONE_STYLES: Record<Tone, { box: string; icon: React.ComponentType<{ className?: string }> }> = {
  neutral: { box: 'border-border bg-secondary/40', icon: Info },
  info: { box: 'border-sky-500/30 bg-sky-500/[0.06]', icon: Info },
  success: { box: 'border-emerald-500/30 bg-emerald-500/[0.06]', icon: ShieldCheck },
  warning: { box: 'border-amber-500/30 bg-amber-500/[0.07]', icon: FileWarning },
  danger: { box: 'border-rose-500/30 bg-rose-500/[0.06]', icon: AlertTriangle },
};

function Meta({ check }: { check: Check }) {
  const rows: [string, React.ReactNode][] = [
    ['Passport No.', <span className="font-mono">{check.passport_no}</span>],
    ['Passport expiry', <span className="font-mono">{check.passport_expiry}</span>],
    ['Nationality', `${check.nationality_text} (${check.nationality_code})`],
    ['Submitted', formatDateTime(check.created_at)],
    ['Started', formatDateTime(check.started_at)],
    ['Finished', formatDateTime(check.finished_at)],
    ['Attempts', check.attempts],
    ['Coins charged', check.coins_charged],
    ...(check.outcome ? ([['Outcome', OUTCOME_LABEL[check.outcome]]] as [string, React.ReactNode][]) : []),
    ...(check.result_source === 'screenshot' ? ([['Result from', 'Portal screenshot']] as [string, React.ReactNode][]) : []),
    ...(check.username ? ([['Account', `@${check.username}`]] as [string, React.ReactNode][]) : []),
  ];
  return (
    <dl className="divide-y text-sm">
      {rows.map(([k, v]) => (
        <div key={k} className="flex items-center justify-between gap-4 py-2.5">
          <dt className="text-muted-foreground">{k}</dt>
          <dd className="text-right font-medium">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

export function CheckDetailPage({ backTo = '/checks', backLabel = 'My checks' }: { backTo?: string; backLabel?: string }) {
  const { id = '' } = useParams();
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [celebrate, setCelebrate] = React.useState(false);
  const wasInProgress = React.useRef(false);

  const q = useQuery({
    queryKey: ['check', id],
    queryFn: () => checksApi.get(id),
    refetchInterval: (query) => {
      const c = query.state.data;
      if (!c) return false;
      if (isInProgress(c.status)) return 4000;
      return c.screenshot_status === 'pending' ? 15_000 : false; // waiting for an admin
    },
  });
  const pricing = useQuery({ queryKey: ['pricing'], queryFn: coinsApi.pricing, staleTime: 60_000 });
  const check = q.data;

  // Celebrate only when we watched the check finish as ACTIVE, not on a revisit.
  React.useEffect(() => {
    if (!check) return;
    if (isInProgress(check.status) || check.screenshot_status === 'pending') {
      wasInProgress.current = true;
      return;
    }
    if (wasInProgress.current) {
      wasInProgress.current = false;
      qc.invalidateQueries({ queryKey: ME_KEY }); // a failure refunds coins
      qc.invalidateQueries({ queryKey: ['checks'] });
      if (isVisaActive(check)) {
        setCelebrate(true);
        toast.success('Visa is ACTIVE!', { description: 'Your status report is ready to download.' });
        const t = setTimeout(() => setCelebrate(false), 2600);
        return () => clearTimeout(t);
      }
    }
  }, [check, qc]);

  const retry = useMutation({
    mutationFn: () => checksApi.retry(id),
    onSuccess: (c) => {
      qc.setQueryData(['check', id], c);
      qc.invalidateQueries({ queryKey: ME_KEY });
      toast.success('Retrying', { description: 'The check is back in the queue.' });
    },
    onError: (err) => {
      if (isApiError(err, 402)) {
        toast.error('Not enough coins to retry', { action: { label: 'Buy coins', onClick: () => navigate('/coins/buy') } });
      } else {
        toast.error(userMessage(err, 'Retry failed'));
        if (isApiError(err, 409)) q.refetch();
      }
    },
  });

  const [confirmCancel, setConfirmCancel] = React.useState(false);
  const cancel = useMutation({
    mutationFn: () => checksApi.cancel(id),
    onSuccess: (c) => {
      qc.setQueryData(['check', id], c);
      qc.invalidateQueries({ queryKey: ME_KEY }); // coins refunded
      qc.invalidateQueries({ queryKey: ['checks'] });
      setConfirmCancel(false);
      toast.success('Check stopped', { description: 'Any coins it cost were refunded.' });
    },
    onError: (err) => {
      setConfirmCancel(false);
      toast.error(userMessage(err, 'Could not stop the check'));
      if (isApiError(err, 409)) q.refetch(); // it finished in the meantime
    },
  });

  if (q.isPending) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full rounded-2xl" />
      </div>
    );
  }
  if (q.isError || !check) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;

  const summary = summarizeCheck(check);
  const tone = TONE_STYLES[summary.tone];
  const running = isInProgress(check.status);
  const cost = pricing.data?.rules.check_cost_coins ?? 1;
  const isOwn = check.user_id === me.id;

  return (
    <Page className="space-y-6">
      <Celebrate show={celebrate} />
      <Rise className="flex flex-wrap items-center justify-between gap-3 pt-2">
        <Button asChild variant="ghost" size="sm" className="-ml-2">
          <Link to={backTo}>
            <ArrowLeft /> {backLabel}
          </Link>
        </Button>
        <CheckBadge check={check} />
      </Rise>

      <Rise>
        <h1 className="text-2xl font-extrabold tracking-tight sm:text-3xl">
          Visa check <span className="font-mono text-gradient">{check.passport_no}</span>
        </h1>
      </Rise>

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="space-y-6">
          <Rise>
            <Card className="overflow-hidden">
              <CardContent className="space-y-6 pt-6 sm:pt-6">
                <CheckStepper check={check} />

                <AnimatePresence mode="wait">
                  {running ? (
                    <motion.div
                      key="running"
                      initial={{ opacity: 0, scale: 0.96 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.96 }}
                      className="flex flex-col items-center gap-4 py-4 text-center"
                    >
                      <ScanningVisual running={check.status === 'running'} />
                      <div>
                        <p className="text-lg font-bold">{summary.title}</p>
                        <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">{summary.description}</p>
                      </div>
                      <p className="rounded-full bg-secondary px-3 py-1 text-xs font-medium text-muted-foreground">
                        Elapsed <Elapsed since={check.created_at} /> · updates automatically
                      </p>
                      {(isOwn || me.is_admin) && (
                        <Button variant="outline" size="sm" onClick={() => setConfirmCancel(true)} loading={cancel.isPending}>
                          {!cancel.isPending && <OctagonX />} Stop check
                        </Button>
                      )}
                    </motion.div>
                  ) : (
                    <motion.div key="done" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
                      <div className={cn('flex gap-3 rounded-xl border p-4', tone.box)}>
                        <tone.icon className="mt-0.5 size-5 shrink-0" />
                        <div>
                          <p className="font-semibold">{summary.title}</p>
                          {summary.description && <p className="mt-0.5 text-sm text-muted-foreground">{summary.description}</p>}
                          {/* The worker's raw error is for debugging, not for customers */}
                          {me.is_admin && check.status === 'failed' && check.message && (
                            <p className="mt-2 font-mono text-xs text-muted-foreground">{check.message}</p>
                          )}
                        </div>
                      </div>

                      <ResultCard check={check} />

                      {check.report_url && (
                        <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-emerald-500/40 p-5 text-center">
                          <DownloadReportButton check={check} className="w-full sm:w-auto" />
                          <p className="text-xs text-muted-foreground">Bilingual (English/Arabic) PDF. It is a status report, not a visa.</p>
                          {(!check.applicant || !check.photo_url) && (
                            <p className="text-xs font-medium text-amber-700 dark:text-amber-400">
                              {!check.applicant ? 'No applicant details yet' : 'No photo yet'}: add them in the Applicant panel so they appear on the report.
                            </p>
                          )}
                        </div>
                      )}

                      {check.status === 'completed' && !check.report_url && (
                        <div className="flex items-center gap-3 rounded-xl bg-secondary/50 p-4 text-sm text-muted-foreground">
                          <CircleSlash className="size-4 shrink-0" />
                          The status report is only available when the portal reports the file as ACTIVE.
                        </div>
                      )}

                      {canRetry(check.status) && (isOwn || me.is_admin) && check.screenshot_status !== 'pending' && (
                        <div className="flex flex-col gap-3 rounded-xl border p-4 sm:flex-row sm:items-center sm:justify-between">
                          <p className="text-sm text-muted-foreground">
                            {me.is_admin ? 'Admins can retry checks for free.' : `${check.status === 'cancelled' ? 'Run it again?' : 'Try again?'} It costs ${cost} coin${cost === 1 ? '' : 's'}, and is refunded if it fails.`}
                          </p>
                          <Button onClick={() => retry.mutate()} loading={retry.isPending}>
                            {!retry.isPending && <RotateCw />} {check.status === 'cancelled' ? 'Run again' : 'Retry check'}
                          </Button>
                        </div>
                      )}

                      {check.status === 'failed' && (
                        <>
                          {me.is_admin && <ScreenshotReview key={check.screenshot_uploaded_at ?? ''} check={check} />}
                          {!(me.is_admin && check.screenshot_status === 'pending') && <ScreenshotPanel check={check} isOwn={isOwn} cost={cost} />}
                        </>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </CardContent>
            </Card>
          </Rise>
        </div>

        <Rise className="space-y-6">
          <ApplicantCard check={check} />
          <Card>
            <CardHeader>
              <CardTitle>Details</CardTitle>
            </CardHeader>
            <CardContent>
              <Meta check={check} />
            </CardContent>
          </Card>
        </Rise>
      </div>

      <Dialog open={confirmCancel} onOpenChange={(v) => !cancel.isPending && setConfirmCancel(v)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Stop this check?</DialogTitle>
            <DialogDescription>
              {check.status === 'running'
                ? 'The browser on the ICP portal is closed and the search is abandoned.'
                : 'The check is taken out of the queue before it starts.'}{' '}
              Any coins it cost are refunded. You can run it again later.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setConfirmCancel(false)} disabled={cancel.isPending}>
              Keep running
            </Button>
            <Button type="button" variant="destructive" onClick={() => cancel.mutate()} loading={cancel.isPending}>
              Stop check
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Page>
  );
}
